/* eslint-disable react/no-array-index-key */
import { FC, useMemo } from 'react';
import { CircularProgress } from '@mui/material';

import { StageEntryDto } from 'src/generated/services/TFinancialApi';
import { useProcessingStages } from 'src/use-cases/invoice/useProcessingStages';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { SingleStageItem } from './SingleStageItem';
import { ReviewRoundsGroup } from './ReviewRoundsGroup';

export const ProcessingStagesMain: FC = () => {
  const { invoiceSummary } = useInvoiceContext();
  const { data: processingStages, isLoading } = useProcessingStages(invoiceSummary?.id);

  const stageGroups = useMemo(() => {
    const stages = processingStages?.stages || [];
    if (!stages.length) return [];

    type GroupItem = { type: 'single'; stage: string; entries: StageEntryDto[] } | { type: 'rounds'; entries: StageEntryDto[] };

    const result: GroupItem[] = [];
    const singleGrouped: Record<string, StageEntryDto[]> = {};
    const singleOrder: string[] = [];
    const roundEntries: StageEntryDto[] = [];

    stages.forEach((entry) => {
      if (entry.roundNumber == null) {
        const key = entry.stage || 'Unknown';
        if (!singleGrouped[key]) {
          singleGrouped[key] = [];
          singleOrder.push(key);
        }
        singleGrouped[key].push(entry);
      } else {
        roundEntries.push(entry);
      }
    });

    singleOrder.forEach((stage) => {
      result.push({ type: 'single', stage, entries: singleGrouped[stage] });
    });

    if (roundEntries.length > 0) {
      result.push({ type: 'rounds', entries: roundEntries });
    }

    return result;
  }, [processingStages?.stages]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center flex-1 py-60'>
        <CircularProgress size={40} />
      </div>
    );
  }

  if (!stageGroups.length) {
    return (
      <div className='flex items-center justify-center flex-1 py-60'>
        <p className='text-14 text-neutral-50'>No processing stages available.</p>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-y-auto px-24 pt-24 pb-40'>
      <div className='mb-12 border border-neutral-20 rounded-8 overflow-hidden bg-white'>
        <div className='w-full p-16 flex items-center justify-between bg-neutral-10 hover:bg-neutral-20 transition-colors'>
          <h2 className='text-14 font-bold text-neutral-90'>Processing Stage</h2>
        </div>
        <div className='p-24 border-t border-neutral-20'>
          {stageGroups.map((group) =>
            group.type === 'single' ? (
              group.entries.map((entry, entryIdx) => <SingleStageItem key={`${group.stage}-${entryIdx}`} stage={group.stage} entry={entry} />)
            ) : (
              <ReviewRoundsGroup key='review-rounds' entries={group.entries} />
            )
          )}
        </div>
      </div>
    </div>
  );
};
